import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Activity,
  AlertCircle,
  ArrowUp,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  LayoutDashboard,
  Package,
  Plus,
  RefreshCw,
  Route,
  Search,
  Users,
  Zap,
} from "lucide-react";

import { DispatchCenterFilter } from "@/components/dispatch-center-filter";
import type { CarrierOperator, CarrierTask, CarrierTrip } from "@/api/carrier";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CreateTripDialog } from "@/features/create-trip-dialog";
import { COMPACT_VIEWPORT_QUERY, useMediaQuery } from "@/lib/responsive";
import { cn } from "@/lib/utils";
import {
  type DispatchWindowFilter,
  dispatchDateKey,
  dispatchWindowFilterValue,
  parseDispatchWindowFilter,
} from "./dispatch-read-model";
import {
  isActiveTrip,
  isOpenTask,
  isOverdue,
  isPlannedTrip,
} from "./dispatch-presenters";
import type { DispatchFreshness } from "./dispatch-live-state";

export interface DispatchWindowOption {
  value: `${string}|${string}`;
  label: string;
}

export function CommandBar({
  lookupActive,
  carrierLabel,
  selectedDate,
  operationalTimeZone,
  selectedWindow,
  windows,
  onDateChange,
  onWindowChange,
  onToggleLookup,
  liveState,
  refreshPending,
  refreshFeedback,
  onRefresh,
}: {
  lookupActive: boolean;
  carrierLabel: string;
  selectedDate: string;
  operationalTimeZone?: string | null;
  selectedWindow: DispatchWindowFilter;
  windows: DispatchWindowOption[];
  onDateChange: (date: string) => void;
  onWindowChange: (window: DispatchWindowFilter) => void;
  onToggleLookup: () => void;
  liveState: DispatchFreshness;
  refreshPending: boolean;
  refreshFeedback: "success" | "error" | null;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const compactViewport = useMediaQuery(COMPACT_VIEWPORT_QUERY);
  // 1024–1279: the full desktop bar wants ~1300px, so between the compact
  // breakpoint and a comfortable desktop the labels stand down and the icons
  // stay. Everything remains reachable; nothing is removed.
  const dense = useMediaQuery("(max-width: 1279px)");

  if (compactViewport) {
    return (
      <>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-zinc-200 bg-white/95 px-2 shadow-sm">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-yellow-50 text-yellow-700">
              <Zap className="h-5 w-5" />
            </div>
            <span className="truncate text-sm font-semibold">
              {carrierLabel}
            </span>
          </div>
          <ConnectionStatus state={liveState} />
          <Button
            type="button"
            variant="ghost"
            size="icon-lg"
            className="h-11 w-11"
            aria-label="Refresh dispatch data"
            disabled={refreshPending}
            onClick={onRefresh}
          >
            <RefreshCw
              className={cn("h-4 w-4", refreshPending && "animate-spin")}
            />
          </Button>
          <Button
            type="button"
            size="icon-lg"
            className="h-11 w-11"
            aria-label="Create trip"
            onClick={() => setCreating(true)}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon-lg"
            className="h-11 w-11"
            aria-label="Exit to dashboard"
            onClick={() => navigate("/fulfillment/orders")}
          >
            <LayoutDashboard className="h-4 w-4" />
          </Button>
        </header>
        {refreshFeedback && (
          <div
            role="status"
            className={cn(
              "shrink-0 px-3 py-1 text-center text-[11px] font-medium",
              refreshFeedback === "success"
                ? "bg-emerald-50 text-emerald-700"
                : "bg-red-50 text-red-700"
            )}
          >
            {refreshFeedback === "success"
              ? "Dispatch data updated"
              : "Refresh failed — try again"}
          </div>
        )}
        <CreateTripDialog
          open={creating}
          onOpenChange={setCreating}
          dialogPresentation="responsive-sheet"
          responsiveSheetAt="tablet"
        />
      </>
    );
  }

  return (
    <>
      <header className="flex h-16 items-center gap-3 border-b border-zinc-200 bg-white/95 px-4 shadow-sm">
        <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-200 bg-yellow-50 text-yellow-700">
            <Zap className="h-5 w-5" />
          </div>
          {/*
            Where the admin console puts its carrier picker. The Carrier Console
            has no picker — the logged-in user's carrier is implicit — so it
            keeps the shape and drops the interaction.
          */}
          <span className="inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-zinc-200 bg-white px-3 text-[13px] font-semibold text-zinc-700">
            {carrierLabel}
          </span>
          <DispatchCenterFilter
            // Same trap as the window select: the trigger's height is set by
            // `lg:data-[size=sm]:h-8`, so only a selector of equal weight can
            // raise it back to the 40px every other control on this bar uses.
            triggerClassName={cn(
              "rounded-full text-[13px] font-semibold lg:data-[size=sm]:h-10",
              dense ? "min-w-[8.5rem]" : "min-w-[10.5rem]"
            )}
          />

          {/*
            Date scopes every scheduled Task. The adjacent time-window control
            is pickup-only because only pickup work carries a booking window.
          */}
          <div className="flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 py-1 pl-2.5 pr-1">
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-bold uppercase tracking-wide text-blue-700">
              <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.8} />
              Pickups
            </span>
            <DayStepper
              selectedDate={selectedDate}
              operationalTimeZone={operationalTimeZone}
              dense={dense}
              onDateChange={onDateChange}
            />
            <div className="relative block">
              <Clock className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <NativeSelect
                aria-label="Time window filter"
                value={dispatchWindowFilterValue(selectedWindow)}
                onChange={(event) =>
                  onWindowChange(parseDispatchWindowFilter(event.target.value))
                }
                // `density` sets a RESPONSIVE height (md:h-8), so an
                // unprefixed h-10 loses at every width that matters. The
                // prefix has to match to win.
                className={cn(
                  "h-10 w-auto max-w-[13rem] rounded-full pl-8 text-[13px] font-semibold md:h-10 md:text-[13px]",
                  dense ? "min-w-[8.5rem]" : "min-w-[10.5rem]"
                )}
              >
                <option value="all">All windows</option>
                {windows.map((window) => (
                  <option key={window.value} value={window.value}>
                    {window.label}
                  </option>
                ))}
                <option value="overdue">Overdue</option>
              </NativeSelect>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <FreshnessControl
            state={liveState}
            pending={refreshPending}
            dense={dense}
            onRefresh={onRefresh}
          />
          {refreshFeedback && (
            <span
              className={cn(
                "text-[11px] font-medium",
                refreshFeedback === "success"
                  ? "text-emerald-700"
                  : "text-red-700"
              )}
            >
              {refreshFeedback === "success" ? "Updated" : "Refresh failed"}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={lookupActive}
            aria-label="Lookup"
            title="Lookup (⌘K)"
            className={cn(
              "h-10 justify-start gap-2 rounded-full text-[13px] font-normal",
              dense ? "w-10 justify-center px-0" : "w-[184px] pl-3 pr-2",
              lookupActive
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "text-zinc-500"
            )}
            onClick={onToggleLookup}
          >
            <Search
              className={cn(
                "h-4 w-4",
                lookupActive ? "text-indigo-500" : "text-zinc-400"
              )}
            />
            {!dense && (
              <>
                Lookup
                <kbd className="ml-auto rounded border bg-white px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">
                  ⌘K
                </kbd>
              </>
            )}
          </Button>
          <Button
            size="sm"
            className="h-10 gap-1.5 rounded-full px-3.5 text-[13px] font-semibold"
            onClick={() => setCreating(true)}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            Trip
          </Button>
          <div className="mx-1 h-7 w-px bg-zinc-200" />
          <Button
            variant="outline"
            size="icon-sm"
            className="h-10 w-10 rounded-full text-muted-foreground"
            onClick={() => navigate("/fulfillment/orders")}
            title="Exit portal"
            aria-label="Exit to dashboard"
          >
            <LayoutDashboard className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <CreateTripDialog
        open={creating}
        onOpenChange={setCreating}
        dialogPresentation="responsive-sheet"
        responsiveSheetAt="tablet"
      />
    </>
  );
}

const CONNECTION_META: Record<
  DispatchFreshness,
  { label: string; dot: string }
> = {
  connected: { label: "Live", dot: "bg-emerald-500" },
  stale: { label: "Stale", dot: "bg-amber-500" },
  reconnecting: { label: "Reconnecting", dot: "bg-amber-500 animate-pulse" },
  disconnected: { label: "Disconnected", dot: "bg-red-500" },
};

/**
 * Data freshness and the refresh, as one control.
 *
 * They were two: a chip reporting the data was stale, and a button beside it
 * that fixed exactly that. One idea rendered twice, costing width on a bar
 * that has none to spare.
 *
 * It stays a status first — the state is announced whether or not anyone
 * presses it — and the button wrapping it is what acts on the state.
 */
function FreshnessControl({
  state,
  pending,
  dense,
  onRefresh,
}: {
  state: DispatchFreshness;
  pending: boolean;
  dense: boolean;
  onRefresh: () => void;
}) {
  const meta = CONNECTION_META[state];
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label="Refresh dispatch data"
      title={`${meta.label} — refresh dispatch data`}
      disabled={pending}
      onClick={onRefresh}
      className={cn(
        "h-10 gap-2 rounded-full text-[12px] font-semibold text-zinc-600",
        dense ? "px-2.5" : "pl-3 pr-2"
      )}
    >
      <span
        role="status"
        aria-label={`Dispatch connection: ${meta.label}`}
        className="flex items-center gap-1.5"
      >
        <span className={cn("h-2 w-2 rounded-full", meta.dot)} />
        {!dense && meta.label}
      </span>
      {!dense && <span className="h-4 w-px bg-zinc-200" />}
      <RefreshCw className={cn("h-3.5 w-3.5", pending && "animate-spin")} />
    </Button>
  );
}

function ConnectionStatus({ state }: { state: DispatchFreshness }) {
  const meta = CONNECTION_META[state];
  return (
    <Badge
      variant="outline"
      role="status"
      aria-label={`Dispatch connection: ${meta.label}`}
      className="h-8 gap-1.5 rounded-full bg-white px-2.5 text-[11.5px] font-semibold text-zinc-600"
    >
      <span className={cn("h-2 w-2 rounded-full", meta.dot)} />
      {meta.label}
    </Badge>
  );
}

function Kpi({
  icon,
  value,
  label,
  tone,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  tone: string;
}) {
  return (
    <div className="flex h-full shrink-0 items-center gap-2 px-3.5">
      <span className={cn("shrink-0", tone)}>{icon}</span>
      <div className="leading-none">
        <span className={cn("text-base font-bold tabular-nums", tone)}>
          {value}
        </span>
        <span className="ml-1.5 whitespace-nowrap text-[11px] font-medium uppercase text-zinc-400">
          {label}
        </span>
      </div>
    </div>
  );
}

/** Keep a valid active day while the native input is briefly empty mid-edit. */
export function DispatchDateInput({
  selectedDate,
  onDateChange,
  className,
  id,
}: {
  selectedDate: string;
  onDateChange: (date: string) => void;
  className?: string;
  id?: string;
}) {
  const [draftDate, setDraftDate] = useState(selectedDate);
  useEffect(() => setDraftDate(selectedDate), [selectedDate]);

  return (
    <Input
      type="date"
      id={id}
      aria-label="Dispatch date"
      value={draftDate}
      onChange={(event) => {
        const date = event.target.value;
        setDraftDate(date);
        if (date) onDateChange(date);
      }}
      onBlur={() => {
        if (!draftDate) setDraftDate(selectedDate);
      }}
      className={className}
    />
  );
}

/** Step through dispatch days; the exact date remains in the popover. */
function DayStepper({
  selectedDate,
  operationalTimeZone,
  dense,
  onDateChange,
}: {
  selectedDate: string;
  operationalTimeZone?: string | null;
  dense: boolean;
  onDateChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const today = dispatchDateKey(new Date(), operationalTimeZone);
  const isToday = today != null && selectedDate === today;

  const step = (days: number) => {
    // Noon UTC, so a day step never lands on a DST boundary and slips a day.
    const at = new Date(`${selectedDate}T12:00:00Z`);
    if (Number.isNaN(at.getTime())) return;
    at.setUTCDate(at.getUTCDate() + days);
    onDateChange(at.toISOString().slice(0, 10));
  };

  const label = (() => {
    if (!selectedDate) return "Select date";
    const at = new Date(`${selectedDate}T12:00:00Z`);
    if (Number.isNaN(at.getTime())) return selectedDate;
    const base = at.toLocaleDateString(undefined, {
      // The weekday is the first thing to go: the date beneath it is what a
      // dispatcher is actually reading.
      ...(dense ? {} : { weekday: "short" }),
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return isToday ? `Today · ${base}` : base;
  })();

  return (
    <div className="inline-flex h-10 shrink-0 items-center overflow-hidden rounded-full border border-zinc-200 bg-white">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Previous day"
        disabled={!selectedDate}
        className="h-full w-8 rounded-none"
        onClick={() => step(-1)}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex h-full items-center gap-1.5 whitespace-nowrap px-1.5 text-[13px] font-semibold text-zinc-800"
          >
            <CalendarDays className="h-4 w-4 text-zinc-400" />
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-auto p-3">
          <div className="flex flex-col gap-2">
            <DispatchDateInput
              selectedDate={selectedDate}
              onDateChange={onDateChange}
              className="h-9 text-[13px]"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isToday || !today}
              onClick={() => {
                if (!today) return;
                onDateChange(today);
                setOpen(false);
              }}
            >
              Jump to today
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Next day"
        disabled={!selectedDate}
        className="h-full w-8 rounded-none"
        onClick={() => step(1)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function KpiStrip({
  tasks,
  trips,
  operators,
}: {
  tasks: CarrierTask[];
  trips: CarrierTrip[];
  operators: CarrierOperator[];
}) {
  const now = Date.now();
  const openTasks = tasks.filter(isOpenTask);
  const unassignedCount = openTasks.filter((task) => !task.tripId).length;
  const overdueCount = openTasks.filter((task) => isOverdue(task, now)).length;
  const activeTrips = trips.filter(isActiveTrip).length;
  const plannedTrips = trips.filter(isPlannedTrip).length;
  const onDuty = operators.filter((op) => op.status === "ON_DUTY").length;

  return (
    <div className="z-20 flex h-10 shrink-0 items-stretch divide-x divide-zinc-100 overflow-x-auto border-b border-zinc-200 bg-white">
      <Kpi
        icon={<Package className="h-4 w-4" />}
        value={unassignedCount}
        label="Unassigned"
        tone="text-orange-600"
      />
      <Kpi
        icon={<AlertCircle className="h-4 w-4" />}
        value={overdueCount}
        label="Overdue"
        tone="text-red-600"
      />
      <Kpi
        icon={<Route className="h-4 w-4" />}
        value={activeTrips}
        label="Active trips"
        tone="text-indigo-600"
      />
      <Kpi
        icon={<Activity className="h-4 w-4" />}
        value={plannedTrips}
        label="Planned"
        tone="text-zinc-800"
      />
      <Kpi
        icon={<Users className="h-4 w-4" />}
        value={`${onDuty}/${operators.length}`}
        label="On duty"
        tone="text-green-600"
      />
      <div className="min-w-0 flex-1" />
    </div>
  );
}
